import {
  Zap, UserPlus, Filter, HelpCircle, Send, ArrowRightLeft, CalendarCheck,
  Megaphone, Layers, Database, ArrowRight, ArrowUpRight, Quote,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { scrollToForm, trackEvent } from "@/lib/tracking";
import { TESTIMONIALS } from "@/components/sections/Testimonials";

const G = "#25d366";

const Eyebrow = ({ children }) => (
  <div className="text-[11px] tracking-[0.2em] uppercase text-zinc-500 font-mono mb-4">{children}</div>
);

const H2 = ({ children, className = "" }) => (
  <h2 className={`font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-[-0.03em] leading-[1.05] text-zinc-900 ${className}`}>
    {children}
  </h2>
);

export function SectionCta({ label, location, testid, dark = false }) {
  const go = () => {
    trackEvent("cta_click", { location, service: "whatsapp" });
    scrollToForm();
  };
  return (
    <Button
      onClick={go}
      data-testid={testid}
      className={`rounded-full whitespace-normal h-auto min-h-12 py-3 px-6 text-center text-base font-semibold transition-[background-color,box-shadow] ${
        dark ? "bg-white hover:bg-zinc-100 text-zinc-900" : "bg-[#25d366] hover:bg-[#1ebe5d] text-[#04220f] shadow-[0_8px_24px_rgba(37,211,102,0.35)]"
      }`}
    >
      {label}
      <ArrowUpRight className="ml-1 h-5 w-5" />
    </Button>
  );
}

/* =================== 2. PROBLEMS WE SOLVE =================== */

const PROBLEMS = [
  { problem: "Customers wait too long for replies", solution: "Automate initial responses" },
  { problem: "Staff repeatedly answer the same questions", solution: "Create automated FAQ conversations" },
  { problem: "Potential customers disappear after enquiring", solution: "Configure appropriate follow-up workflows" },
  { problem: "Sales teams receive incomplete enquiries", solution: "Collect and qualify customer information" },
  { problem: "Conversations are difficult to manage", solution: "Route enquiries and provide human handover" },
  { problem: "Businesses struggle to track enquiries", solution: "Connect lead capture with available reporting tools" },
];

export function WhatsappProblem() {
  return (
    <section data-testid="wa-problem-section" className="relative py-20 sm:py-28 bg-sky-wash">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="max-w-3xl mb-12">
          <Eyebrow>Problems we solve</Eyebrow>
          <H2>Are WhatsApp Enquiries Taking Too Much of Your Time?</H2>
          <p className="mt-6 text-zinc-600 leading-relaxed text-base sm:text-lg">
            Customers expect businesses to respond quickly. But when your team is busy, enquiries can go unanswered,
            follow-ups can be missed and employees can spend hours repeating the same information.
          </p>
          <p className="mt-3 text-zinc-600 leading-relaxed">
            Rekisa helps businesses reduce these manual tasks through WhatsApp customer service automation.
          </p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {PROBLEMS.map((p, i) => (
            <div key={p.problem} data-testid={`wa-problem-card-${i}`} className="rounded-2xl border border-zinc-200 bg-white p-6 shadow-sm">
              <div className="text-[10px] tracking-[0.2em] uppercase font-mono font-bold text-[#b3261e]">Problem</div>
              <h3 className="mt-1.5 font-display text-lg font-bold text-zinc-900 leading-snug">{p.problem}</h3>
              <div className="mt-4 pt-4 border-t border-zinc-100 flex items-start gap-2">
                <ArrowRight className="h-4 w-4 mt-0.5 shrink-0" style={{ color: G }} />
                <p className="text-[15px] text-zinc-700"><span className="font-semibold text-zinc-900">Solution:</span> {p.solution}</p>
              </div>
            </div>
          ))}
        </div>
        <div className="mt-10">
          <SectionCta label="Discuss Your WhatsApp Requirements" location="wa_problems" testid="wa-problems-cta" />
        </div>
      </div>
    </section>
  );
}

/* =================== 4. FEATURES =================== */

const TIERS = {
  standard: { label: "Standard", color: "#34a853" },
  advanced: { label: "Advanced configuration", color: "#1a73e8" },
  additional: { label: "May need extra software", color: "#b45309" },
};

const FEATURES = [
  { title: "Automated Customer Replies", desc: "Respond to common enquiries without requiring an employee to type every response.", Icon: Zap, tier: "standard" },
  { title: "WhatsApp Lead Capture", desc: "Collect customer details and enquiry information.", Icon: UserPlus, tier: "standard" },
  { title: "Lead Qualification", desc: "Ask questions that help identify customer requirements.", Icon: Filter, tier: "standard" },
  { title: "Customer Support Automation", desc: "Answer frequently asked service, pricing or product questions.", Icon: HelpCircle, tier: "standard" },
  { title: "Human Handover", desc: "Transfer conversations to team members when necessary.", Icon: ArrowRightLeft, tier: "standard" },
  { title: "Automated Follow-Ups", desc: "Configure permitted reminders and follow-up messages.", Icon: Send, tier: "advanced", policy: true },
  { title: "Appointment Requests", desc: "Help customers request bookings or appointments.", Icon: CalendarCheck, tier: "advanced" },
  { title: "Customer Segmentation", desc: "Organise contacts based on their interests or enquiry status.", Icon: Layers, tier: "advanced" },
  { title: "CRM & Lead Integration", desc: "Connect enquiries to supported CRMs or lead-management tools.", Icon: Database, tier: "additional" },
  { title: "WhatsApp Broadcasts", desc: "Send permitted messages to appropriately opted-in customers.", Icon: Megaphone, tier: "additional", policy: true },
];

export function WhatsappFeatures() {
  return (
    <section id="features" data-testid="wa-automate-section" className="relative py-24 sm:py-32 bg-zinc-50/60 border-y border-zinc-200">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="max-w-3xl mb-10">
          <Eyebrow>WhatsApp automation services</Eyebrow>
          <H2>What Can You Automate on WhatsApp?</H2>
          <p className="mt-6 text-zinc-600 leading-relaxed text-base sm:text-lg">
            Rekisa configures WhatsApp automation around the conversations your business handles every day — from lead capture
            and automated follow-up to support questions and human handover.
          </p>
        </div>
        <div className="flex flex-wrap gap-2 mb-8" data-testid="wa-feature-legend">
          {Object.values(TIERS).map((t) => (
            <span key={t.label} className="inline-flex items-center gap-2 rounded-full border border-zinc-200 bg-white px-3 py-1.5 text-xs font-semibold text-zinc-700">
              <span className="h-2 w-2 rounded-full" style={{ background: t.color }} /> {t.label}
            </span>
          ))}
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4">
          {FEATURES.map(({ title, desc, Icon, tier, policy }, idx) => (
            <div key={title} data-testid={`wa-feature-${idx}`}
              className="group relative rounded-2xl border border-zinc-200 bg-white p-5 hover:border-zinc-300 hover:shadow-[0_20px_50px_-25px_rgba(0,0,0,0.15)] transition-[box-shadow,border-color] flex flex-col">
              <div className="h-10 w-10 rounded-xl flex items-center justify-center mb-4 transition-transform group-hover:scale-110" style={{ background: `${G}15`, color: G }}>
                <Icon className="h-5 w-5" />
              </div>
              <h3 className="font-display text-base font-bold text-zinc-900 tracking-tight">{title}</h3>
              <p className="mt-1.5 text-[13px] text-zinc-600 leading-relaxed flex-1">{desc}</p>
              <span className="mt-4 inline-flex items-center gap-1.5 text-[11px] font-semibold" style={{ color: TIERS[tier].color }}>
                <span className="h-1.5 w-1.5 rounded-full" style={{ background: TIERS[tier].color }} />
                {TIERS[tier].label}{policy ? " · Meta rules apply" : ""}
              </span>
            </div>
          ))}
        </div>
        <div data-testid="wa-features-policy-note" className="mt-8 rounded-2xl border border-[#fbbc04]/40 bg-[#fbbc04]/[0.08] p-5 text-sm text-zinc-800 leading-relaxed">
          <span className="font-bold text-zinc-900">Good to know: </span>
          The starting price covers the standard features. Advanced configuration and integrations are quoted to scope, and some need
          their own software subscription. Outbound follow-ups and broadcasts are subject to Meta's WhatsApp messaging policies,
          customer opt-in, approved message templates and WhatsApp platform charges.
        </div>
      </div>
    </section>
  );
}

/* =================== 6. PROCESS =================== */

const PROCESS = [
  { title: "Understand Your Business", desc: "We discuss your customer enquiries, challenges and what you want to automate.", color: "#25d366" },
  { title: "Design Your WhatsApp Journey", desc: "We map the questions, replies, lead capture, qualification and handover process.", color: "#4285f4" },
  { title: "Build Your Automation", desc: "We configure the agreed workflows using Chatfuel and connect the supported systems included in your package.", color: "#fbbc04" },
  { title: "Test and Launch", desc: "We test the customer experience and prepare the automation for use.", color: "#ea4335" },
  { title: "Review and Improve", desc: "We assess the initial setup and identify opportunities for improvement, according to your support agreement.", color: "#34a853" },
];

export function WhatsappProcess() {
  return (
    <section id="how-it-works" data-testid="wa-process-section" className="relative py-24 sm:py-32 bg-mint-wash">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="max-w-3xl mb-12">
          <Eyebrow>WhatsApp automation setup · Chatfuel implementation</Eyebrow>
          <H2>How Rekisa Sets Up Your WhatsApp Automation</H2>
        </div>
        <ol className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4">
          {PROCESS.map((s, i) => (
            <li key={s.title} data-testid={`wa-process-step-${i + 1}`}
              className="relative rounded-2xl border border-zinc-200 bg-white p-6 overflow-hidden">
              <span aria-hidden className="absolute left-0 top-0 bottom-0 w-1" style={{ background: s.color }} />
              <div className="text-[10px] tracking-[0.2em] uppercase font-mono mb-2 font-bold" style={{ color: s.color }}>Step {i + 1}</div>
              <h3 className="font-display text-lg font-bold text-zinc-900 tracking-tight">{s.title}</h3>
              <p className="mt-1.5 text-sm text-zinc-600 leading-relaxed">{s.desc}</p>
            </li>
          ))}
        </ol>
        <div className="mt-10">
          <SectionCta label="Request Your Implementation Quote" location="wa_process" testid="wa-process-cta" />
        </div>
      </div>
    </section>
  );
}

/* =================== 8. WHY REKISA =================== */

const PROOF = [
  "Experience across digital marketing, eCommerce, CRM systems and automation",
  "We design the conversations, information capture and follow-up — not just the bot",
  "Practical, scoped implementation for South African SMEs",
  "Implemented by Rekisa on Chatfuel, a dedicated WhatsApp automation platform",
];

const TESTIMONIAL_PICKS = [
  { id: "sabo", service: "eCommerce & strategy" },
  { id: "mashesha", service: "Sales & marketing outreach" },
  { id: "innovative", service: "Sales & marketing process" },
];

export function WhatsappWhy() {
  const picks = TESTIMONIAL_PICKS.map((p) => ({ ...TESTIMONIALS.find((t) => t.id === p.id), service: p.service }));
  return (
    <section data-testid="wa-why-section" className="relative py-24 sm:py-32 bg-white">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="grid lg:grid-cols-[1fr_1fr] gap-12 lg:gap-20 items-start">
          <div>
            <Eyebrow>Why Rekisa · WhatsApp implementation services</Eyebrow>
            <H2>More Than a WhatsApp Chatbot. A Better Way to Manage Customer Enquiries.</H2>
          </div>
          <div>
            <p className="text-zinc-700 leading-relaxed text-lg">
              Rekisa helps South African businesses build practical digital systems for customer communication, sales and growth.
              Our approach combines experience in digital marketing, eCommerce, CRM systems and automation.
            </p>
            <p className="mt-4 text-zinc-700 leading-relaxed text-lg">
              We don't simply install a chatbot. We help businesses design the conversations, information capture and follow-up
              processes needed to support their customers.
            </p>
            <ul className="mt-8 space-y-3">
              {PROOF.map((p, i) => (
                <li key={p} data-testid={`wa-proof-${i}`} className="flex items-start gap-3 text-[15px] text-zinc-800">
                  <span className="h-6 w-6 rounded-full flex items-center justify-center font-mono font-bold text-[11px] shrink-0" style={{ background: `${G}18`, color: "#128c4b" }}>{i + 1}</span>
                  {p}
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="mt-16">
          <div className="flex flex-wrap items-end justify-between gap-3 mb-6">
            <h3 className="font-display text-2xl font-black text-zinc-900">What clients say about working with Rekisa</h3>
            <p data-testid="wa-testimonial-disclaimer" className="text-xs text-zinc-500 max-w-md">
              These testimonials relate to other Rekisa services, as labelled — not to WhatsApp automation specifically.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-5">
            {picks.map((t) => (
              <figure key={t.id} data-testid={`wa-testimonial-${t.id}`} className="relative rounded-3xl border border-zinc-200 bg-zinc-50/60 p-6">
                <Quote aria-hidden className="absolute top-5 right-5 h-6 w-6 text-zinc-200" />
                <span className="inline-block rounded-full bg-white border border-zinc-200 px-2.5 py-1 text-[10px] font-mono uppercase tracking-[0.14em] text-zinc-600">
                  Service: {t.service}
                </span>
                <blockquote className="mt-4 text-[15px] text-zinc-800 leading-relaxed">“{t.quote}”</blockquote>
                <figcaption className="mt-5 pt-4 border-t border-zinc-200 text-sm">
                  <div className="font-semibold text-zinc-900">{t.author}</div>
                  <div className="text-xs text-zinc-500">{t.role}</div>
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
